import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import RecipeGradeMultiSelect from "../../components/recipe-grade-multi-select/recipe-grade-multi-select";
import {
  applyDefaultCellsFromGroups,
  buildDisplayWorkflowOps,
  resolveRecipeGroupsForStep,
  validateRecipeTnmOrdering,
  type RecipeGroupDef,
  type RecipeParamDef,
  type WoRowLite,
} from "../../lib/recipe-groups";
import { fetchRecipeProcessParameterGroups } from "../../lib/recipe-process-parameters";
import { useMaterialCatalog } from "../../hooks/useMaterialCatalog";
import { getList, getListById, postRecipe } from "../../services/services";
import { ENDPOINT } from "../../services/api-endpoint";

type WorkflowListRow = {
  id: number;
  workflowname?: string | null;
  template_name?: string | null;
  partid?: string | null;
  part_name?: string | null;
  partDisplayName?: string | null;
};

type WoRow = WoRowLite;

function normalizeWorkflowPayload(w: unknown): { id: number; workflowoperations?: WoRow[] } | null {
  if (!w || typeof w !== "object") return null;
  const o = w as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isFinite(id)) return null;
  return w as { id: number; workflowoperations?: WoRow[] };
}

function wfLabel(w: WorkflowListRow): string {
  const name = (w.template_name ?? w.workflowname ?? "").trim() || `Workflow #${w.id}`;
  const part =
    (w.partDisplayName ?? w.part_name ?? "").trim() ||
    (w.partid ? `Part #${w.partid}` : "");
  return part ? `${name} — ${part}` : name;
}

function summarizeTemplate(
  displayOps: WoRowLite[],
  stepGroups: RecipeGroupDef[][] | null,
): {
  paramCount: number;
  categoryCount: number;
  criticalSlots: number;
} {
  const cats = new Set<string>();
  let paramCount = 0;
  let criticalSlots = 0;
  displayOps.forEach((op, sIdx) => {
    for (const g of resolveRecipeGroupsForStep(stepGroups, sIdx, op)) {
      cats.add(g.title);
      for (const p of g.params) {
        paramCount += 1;
        if (p.required) criticalSlots += 1;
      }
    }
  });
  return { paramCount, categoryCount: cats.size, criticalSlots };
}

type Props = {
  show: boolean;
  onHide: () => void;
  onCreated?: () => void;
};

export default function TrurecipeGenerateRecipeModal({ show, onHide, onCreated }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  const [workflows, setWorkflows] = useState<WorkflowListRow[]>([]);
  const [workflowId, setWorkflowId] = useState("");
  const [wfDetail, setWfDetail] = useState<{ id: number; workflowoperations?: WoRow[] } | null>(
    null,
  );

  const [recipeName, setRecipeName] = useState("");
  const [recipeCategoryId, setRecipeCategoryId] = useState("");
  const [recipeGradeIds, setRecipeGradeIds] = useState<number[]>([]);
  const {
    categories: materialCategories,
    grades: materialGrades,
  } = useMaterialCatalog(recipeCategoryId);

  const [recipeCells, setRecipeCells] = useState<Record<string, string>>({});
  const [stepParamGroups, setStepParamGroups] = useState<RecipeGroupDef[][] | null>(null);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [recipeSavedId, setRecipeSavedId] = useState<number | null>(null);
  const [validated, setValidated] = useState(false);
  const [validationTier, setValidationTier] = useState<"idle" | "basic" | "full">("idle");

  const displayOps = useMemo(() => {
    const raw = (wfDetail?.workflowoperations ?? []).filter((o) => o.active !== false);
    return buildDisplayWorkflowOps(raw as WoRowLite[]);
  }, [wfDetail]);

  const partLabel = useMemo(() => {
    const w = workflows.find((x) => String(x.id) === workflowId);
    if (!w) return "—";
    return (
      (w.partDisplayName ?? w.part_name ?? "").trim() ||
      (w.partid ? `Part #${w.partid}` : "—")
    );
  }, [workflows, workflowId]);

  const summary = useMemo(
    () => summarizeTemplate(displayOps, stepParamGroups),
    [displayOps, stepParamGroups],
  );

  const resetAll = useCallback(() => {
    setStep(1);
    setBusy(false);
    setSaveBusy(false);
    setWorkflowId("");
    setWfDetail(null);
    setRecipeName("");
    setRecipeCategoryId("");
    setRecipeGradeIds([]);
    setRecipeCells({});
    setStepParamGroups(null);
    setParamsLoading(false);
    setRecipeSavedId(null);
    setValidated(false);
    setValidationTier("idle");
    setWorkflows([]);
  }, []);

  useEffect(() => {
    if (!show) {
      resetAll();
      return;
    }
    resetAll();
    let cancelled = false;
    void (async () => {
      setBusy(true);
      try {
        const wfResp = await getList(ENDPOINT.WORKFLOWS_LIST, {
          pagesize: 200,
          pagenumber: 0,
          serachtext: "",
        });
        if (cancelled) return;
        const items = Array.isArray(wfResp) ? wfResp : (wfResp as { data?: unknown })?.data;
        setWorkflows(Array.isArray(items) ? (items as WorkflowListRow[]) : []);
      } catch {
        if (!cancelled) {
          setWorkflows([]);
          toast.error("Could not load workflows.");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, resetAll]);

  useEffect(() => {
    setRecipeGradeIds([]);
  }, [recipeCategoryId]);

  useEffect(() => {
    setRecipeGradeIds((prev) => prev.filter((id) => materialGrades.some((g) => g.id === id)));
  }, [materialGrades]);

  useEffect(() => {
    const wid = Number(workflowId);
    if (!show || !Number.isFinite(wid) || wid <= 0) {
      setWfDetail(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const raw = await getListById(ENDPOINT.WORKFLOWS_LIST_BY_ID, wid);
        if (cancelled) return;
        const wfRaw = Array.isArray(raw) ? raw[0] : raw;
        const wf = normalizeWorkflowPayload(wfRaw);
        setWfDetail(wf);
        const w = workflows.find((x) => x.id === wid);
        const base = (w?.template_name ?? w?.workflowname ?? "").trim() || "Workflow";
        setRecipeName((prev) => (prev.trim() ? prev : `${base} — Recipe`));
      } catch {
        if (!cancelled) setWfDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, workflowId, workflows]);

  const goNext = async () => {
    setValidated(true);
    const wid = Number(workflowId);
    const cat = Number(recipeCategoryId);
    const errs: string[] = [];
    if (!Number.isFinite(wid) || wid <= 0) errs.push("Select a workflow template.");
    if (!displayOps.length) errs.push("Wait for workflow steps to load, then try again.");
    if (!Number.isFinite(cat) || cat <= 0) errs.push("Select a material category.");
    if (recipeGradeIds.length === 0) errs.push("Select at least one material grade.");
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    setParamsLoading(true);
    try {
      const groups = await fetchRecipeProcessParameterGroups(wid);
      setStepParamGroups(groups);
      let nextCells = { ...recipeCells };
      displayOps.forEach((op, sIdx) => {
        const stepGroups = resolveRecipeGroupsForStep(groups, sIdx, op);
        nextCells = applyDefaultCellsFromGroups(nextCells, sIdx, stepGroups);
      });
      setRecipeCells(nextCells);
      setValidationTier("idle");
      setStep(2);
    } catch (e) {
      console.log(`Error loading process parameters: ${e}`);
      toast.error("Could not load process parameters for this workflow.");
    } finally {
      setParamsLoading(false);
    }
  };

  const saveRecipe = async (status: "draft" | "approved") => {
    const wid = Number(workflowId);
    const cat = Number(recipeCategoryId);

    setValidated(true);
    setValidationTier(status === "approved" ? "full" : "basic");

    const valErrors: string[] = [];
    if (!Number.isFinite(wid) || wid <= 0) valErrors.push("Select a workflow template.");
    if (!displayOps.length) valErrors.push("No workflow steps to save.");
    if (!Number.isFinite(cat) || cat <= 0) valErrors.push("Select a material category.");
    if (recipeGradeIds.length === 0) valErrors.push("Select at least one material grade.");

    if (status === "approved") {
      for (let sIdx = 0; sIdx < displayOps.length; sIdx++) {
        const op = displayOps[sIdx]!;
        const groups = resolveRecipeGroupsForStep(stepParamGroups, sIdx, op);
        for (const g of groups) {
          for (const p of g.params) {
            if (!p.required) continue;
            const k = `${sIdx}-${p.key}-t`;
            if (!(recipeCells[k] ?? "").trim()) {
              valErrors.push(
                `"${p.label}" in step ${sIdx + 1} needs a target value before approval.`,
              );
            }
          }
        }
      }
    }

    valErrors.push(...validateRecipeTnmOrdering(recipeCells));

    if (valErrors.length) {
      const shown = valErrors.slice(0, 2).join(" ");
      const more = valErrors.length > 2 ? ` (+${valErrors.length - 2} more)` : "";
      toast.error(`${shown}${more}`);
      return;
    }

    const firstGrade =
      recipeGradeIds.find((id) => materialGrades.some((g) => g.id === id)) ?? null;

    setSaveBusy(true);
    try {
      const payload = { cells: { ...recipeCells } };
      const wfRow = workflows.find((w) => w.id === wid);
      const baseName =
        recipeName.trim() ||
        `${(wfRow?.template_name ?? wfRow?.workflowname ?? "Recipe").toString().trim()} — Recipe`;

      let finalId: number;
      if (!recipeSavedId) {
        /** First save is always draft; approve from the recipe detail page or a later edit. */
        const created = await postRecipe({
          workflowId: wid,
          name: baseName,
          materialCategoryId: cat,
          materialGradeId: firstGrade,
          materialGradeIds: recipeGradeIds.length ? recipeGradeIds : undefined,
          status: "draft",
          payload,
        });
        finalId = created.id;
        setRecipeSavedId(created.id);
      } else {
        const saved = await postRecipe({
          id: recipeSavedId,
          workflowId: wid,
          name: baseName,
          materialCategoryId: cat,
          materialGradeId: firstGrade,
          materialGradeIds: recipeGradeIds.length ? recipeGradeIds : undefined,
          status,
          payload,
        });
        finalId = saved.id;
      }
      setValidated(false);
      setValidationTier("idle");
      toast.success(
        !recipeSavedId
          ? "Recipe saved as draft."
          : status === "approved"
            ? "Recipe saved as approved."
            : "Recipe saved as draft.",
      );
      onCreated?.();
      onHide();
      resetAll();
      navigate(`/mes-trurecipe/recipe/${finalId}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save recipe.");
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!saveBusy) onHide();
      }}
      size={step === 2 ? "xl" : "lg"}
      centered
      backdrop="static"
      className={`tv-gen-recipe-modal${step === 2 ? " tv-gen-recipe-modal--step2" : ""}`}
      dialogClassName={`tv-gen-recipe-modal__dialog tv-gen-recipe-modal__dialog--step${step}`}
    >
      <Modal.Header closeButton={false} className="tv-gen-recipe-modal__head">
        <div className="tv-gen-recipe-modal__head-main">
          <Modal.Title className="tv-gen-recipe-modal__title">
            Create Recipe — Step {step} of 2
          </Modal.Title>
          <p className="tv-gen-recipe-modal__sub">
            {step === 1
              ? "Choose a workflow template and material data. Technology and process for each step come from that template on the server."
              : "Enter targets, nominal, and tolerance for each parameter — same layout as workflow recipe authoring."}
          </p>
        </div>
        <button
          type="button"
          className="tv-gen-recipe-modal__close"
          aria-label="Close"
          disabled={saveBusy}
          onClick={() => {
            if (!saveBusy) onHide();
          }}
        >
          <i className="fa fa-times" aria-hidden />
        </button>
      </Modal.Header>
      <Modal.Body className="tv-gen-recipe-modal__body">
        {busy ? (
          <div className="tv-gen-recipe-modal__loading">
            <Spinner animation="border" size="sm" />
            <span>Loading…</span>
          </div>
        ) : step === 1 ? (
          <div className="tv-gen-recipe-modal__step1-fields">
            <Form.Group className="mb-3" controlId="gen-recipe-name">
              <Form.Label className="tv-gen-recipe-modal__label">Recipe name (optional)</Form.Label>
              <Form.Control
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder="Auto-generated if left blank"
                className="tv-gen-recipe-modal__control"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="gen-recipe-wf">
              <Form.Label className="tv-gen-recipe-modal__label">Workflow template</Form.Label>
              <Form.Select
                className="tv-gen-recipe-modal__control tv-gen-recipe-modal__select"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                isInvalid={validated && (!workflowId || !Number.isFinite(Number(workflowId)))}
              >
                <option value="">Select workflow…</option>
                {workflows.map((w) => (
                  <option key={w.id} value={String(w.id)}>
                    {wfLabel(w)}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">Choose a workflow.</Form.Control.Feedback>
            </Form.Group>
            <Row className="g-3 mb-3 tv-gen-recipe-modal__row-split">
              <Col xs={12} md={6}>
                <Form.Group className="mb-0" controlId="gen-recipe-part">
                  <Form.Label className="tv-gen-recipe-modal__label">Part</Form.Label>
                  <Form.Control
                    readOnly
                    className="tv-gen-recipe-modal__control tv-gen-recipe-modal__control--readonly"
                    value={partLabel}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-0" controlId="gen-recipe-cat">
                  <Form.Label className="tv-gen-recipe-modal__label">Material category</Form.Label>
                  <Form.Select
                    className="tv-gen-recipe-modal__control tv-gen-recipe-modal__select"
                    value={recipeCategoryId}
                    onChange={(e) => {
                      setRecipeCategoryId(e.target.value);
                      setRecipeGradeIds([]);
                    }}
                    isInvalid={
                      validated &&
                      (!recipeCategoryId ||
                        !Number.isFinite(Number(recipeCategoryId)) ||
                        Number(recipeCategoryId) <= 0)
                    }
                  >
                    <option value="">Select category…</option>
                    {materialCategories.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {(c.name ?? "").trim() || `Category #${c.id}`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">Select a material category.</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="gen-recipe-grades">
              <Form.Label className="tv-gen-recipe-modal__label">Material grades</Form.Label>
              <div className="tv-gen-recipe-modal__grades-field">
              <RecipeGradeMultiSelect
                disabled={!recipeCategoryId}
                grades={materialGrades}
                value={recipeGradeIds}
                onChange={setRecipeGradeIds}
                invalid={validated && recipeGradeIds.length === 0}
                placeholder={
                  recipeCategoryId ? "Search and select grades…" : "Select a category first"
                }
              />
              </div>
              {validated && recipeGradeIds.length === 0 ? (
                <div className="tv-gen-recipe-modal__field-error">Select at least one grade.</div>
              ) : null}
            </Form.Group>
            {displayOps.length > 0 ? (
              <div className="tv-gen-recipe-modal__hint">
                <strong>{displayOps.length} steps</strong>
                {" — "}
                this workflow defines{" "}
                <strong>{summary.paramCount} parameters</strong> across{" "}
                <strong>{summary.categoryCount} categories</strong>.{" "}
                <strong className="tv-gen-recipe-modal__hint--crit">{summary.criticalSlots} critical</strong>.
              </div>
            ) : workflowId ? (
              <div className="tv-gen-recipe-modal__hint tv-gen-recipe-modal__hint--muted">
                Loading workflow steps…
              </div>
            ) : null}
          </div>
        ) : (
          <div className="tv-gen-recipe-modal__step2">
            {displayOps.map((op, sIdx) => {
              const proc = (op.processDefinitionName ?? "").trim() || "—";
              const tech = (op.mfgTechnologyName ?? "").trim() || "—";
              const groups = resolveRecipeGroupsForStep(stepParamGroups, sIdx, op);
              if (!groups.length) {
                return (
                  <div key={op.id ?? `step-${sIdx}`} className="tv-gen-recipe-modal__step-block">
                    <div className="tv-gen-recipe-modal__hint tv-gen-recipe-modal__hint--muted">
                      Step {sIdx + 1}: no process parameters — assign a machine with technology and
                      model on the workflow step.
                    </div>
                  </div>
                );
              }
              return (
                <div key={op.id ?? `step-${sIdx}`} className="tv-gen-recipe-modal__step-block">
                  <div className="tv-gen-recipe-modal__step-head">
                    <span className="tv-gen-recipe-modal__step-num">Step {sIdx + 1}</span>
                    <strong>{proc}</strong>
                    <span className="tv-gen-recipe-modal__step-tech">{tech}</span>
                  </div>
                  {groups.map((g) => (
                    <div key={g.title} className="tv-gen-recipe-modal__param-group">
                      <div className="tv-gen-recipe-modal__group-title">{g.title}</div>
                      <div className="tv-gen-recipe-modal__param-grid">
                        {g.params.map((p: RecipeParamDef) => {
                          const cell = (slot: "t" | "n" | "x") => `${sIdx}-${p.key}-${slot}`;
                          const invalidT =
                            validated &&
                            validationTier === "full" &&
                            p.required &&
                            !(recipeCells[cell("t")] ?? "").trim();
                          return (
                            <div
                              key={p.key}
                              className={`tv-gen-recipe-modal__param${invalidT ? " is-invalid" : ""}`}
                            >
                              <div
                                className={
                                  p.required
                                    ? "tv-gen-recipe-modal__param-label tv-gen-recipe-modal__param-label--req"
                                    : "tv-gen-recipe-modal__param-label"
                                }
                              >
                                {p.label}
                                {p.unit ? <span className="tv-gen-recipe-modal__unit"> {p.unit}</span> : null}
                              </div>
                              <div className="tv-gen-recipe-modal__tmm">
                                <div className="tv-gen-recipe-modal__tmm-cell">
                                  <Form.Control
                                    size="sm"
                                    placeholder="Target"
                                    value={recipeCells[cell("t")] ?? ""}
                                    onChange={(e) =>
                                      setRecipeCells((prev) => ({
                                        ...prev,
                                        [cell("t")]: e.target.value,
                                      }))
                                    }
                                    className="tv-gen-recipe-modal__control"
                                  />
                                  <span className="tv-gen-recipe-modal__tmm-tag">T</span>
                                </div>
                                <Form.Control
                                  size="sm"
                                  placeholder="Nominal"
                                  value={recipeCells[cell("n")] ?? ""}
                                  onChange={(e) =>
                                    setRecipeCells((prev) => ({
                                      ...prev,
                                      [cell("n")]: e.target.value,
                                    }))
                                  }
                                  className="tv-gen-recipe-modal__control"
                                />
                                <Form.Control
                                  size="sm"
                                  placeholder="Tolerance"
                                  value={recipeCells[cell("x")] ?? ""}
                                  onChange={(e) =>
                                    setRecipeCells((prev) => ({
                                      ...prev,
                                      [cell("x")]: e.target.value,
                                    }))
                                  }
                                  className="tv-gen-recipe-modal__control"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            <p className="tv-gen-recipe-modal__crit-summary">
              New recipes are saved as <strong>draft</strong>. Use{" "}
              <strong>Save Recipe</strong> to require every target before saving. Approve later
              from the recipe page.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="tv-gen-recipe-modal__foot">
        {step === 1 ? (
          <>
            <Button variant="outline-secondary" onClick={onHide} disabled={saveBusy}>
              Cancel
            </Button>
            <Button
              className="tv-gen-recipe-modal__btn-next"
              onClick={() => void goNext()}
              disabled={busy || paramsLoading}
            >
              {paramsLoading ? "Loading parameters…" : "Next — Enter parameters"}{" "}
              <i className="fa fa-angle-right" aria-hidden />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setStep(1);
                setValidated(false);
                setValidationTier("idle");
              }}
              disabled={saveBusy}
            >
              <i className="fa fa-angle-left" aria-hidden /> Back
            </Button>
            <div className="tv-gen-recipe-modal__foot-spacer" />
            <Button
              variant="outline-primary"
              className="me-2"
              disabled={saveBusy}
              onClick={() => void saveRecipe("draft")}
            >
              {saveBusy ? "Saving…" : "Save as draft"}
            </Button>
            <Button className="tv-gen-recipe-modal__btn-next" disabled={saveBusy} onClick={() => void saveRecipe("approved")}>
              {saveBusy ? "Saving…" : "Save Recipe"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
